<!--
VueSelectListMultiple Component - Generic Multi-Select Dropdown Component (PrimeVue MultiSelect)

USE CASES:
==========

1. MULTIPLE FRANCHISE SELECTION:
   - Use for selecting multiple franchises in forms and filters
   - Data source: eventBus.emit('filter.listFranchisesArraySelect')
   - Returns JSON array format:
     [
       {
         "Name": "Pato Branco",
         "ID": "14"
       },
       {
         "Name": "Francisco Beltrão", 
         "ID": "50"
       }
     ]

2. MULTIPLE LOCATION SELECTION:
   - Use for selecting multiple locations/localidades
   - Data source: Location service API endpoints
   - Same JSON array format as franchises

3. MULTIPLE USER SELECTION:
   - Use for selecting multiple users, technicians, or staff members
   - Data source: User management APIs
   - Same JSON array format

4. MULTIPLE CATEGORY SELECTION:
   - Use for selecting service categories, expense categories, etc.
   - Data source: Category management APIs
   - Same JSON array format

5. MULTIPLE STATUS SELECTION:
   - Use for selecting multiple status values
   - Data source: Static arrays or configuration APIs
   - Same JSON array format

FEATURES:
=========
- Uses PrimeVue MultiSelect for enhanced multi-selection UI
- Supports validation with visual indicators
- Handles disabled state
- Supports required field validation
- Emits both v-model and legacy input events
- Responsive layout with optional labels
- Searchable dropdown with filter
- Select All functionality
- Display selected items as chips or comma-separated text
- Maximum selected labels display control

PROPS:
======
- itensSelect: Array of objects with {ID, Name} structure
- elemId: Unique identifier for the select element
- placeholder: Placeholder text for the dropdown
- label: Optional label text (if empty, uses full width)
- validation: Function that returns boolean for validation state
- ngRequired: Boolean/string indicating if field is required
- disabled: Boolean/string indicating if field is disabled
- modelValue: Current selected values array (v-model)
- value: Legacy value prop for backward compatibility
- callback: Optional function to call when value changes
- display: Display mode - 'comma' (default) or 'chip'
- maxSelectedLabels: Maximum number of selected labels to show before using "{n} items selected" format
- filter: Enable/disable filtering (default: true)
- showClear: Show clear button to reset selection (default: false)
- selectAll: Enable/disable select all checkbox (default: true)
-->

<template>
  <div class="select-list-multiple-wrapper">
    <!-- Label with validation indicators -->
    <label 
      v-if="label && label !== ''" 
      :for="elemId" 
      class="select-list-label"
      :class="{ 
        'text-warning': !hasValidation && isRequired, 
        'text-success': hasValidation && isRequired
      }"
    >
      <i v-if="isRequired && !hasValidation" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-indicator">*</span>
      {{ label }}
    </label>

    <!-- PrimeVue MultiSelect -->
    <MultiSelect
      :inputId="elemId"
      v-model="selectedItems"
      :options="props.itensSelect"
      optionLabel="Name"
      optionValue="ID"
      :disabled="isDisabled"
      :placeholder="placeholder || ''"
      :invalid="!hasValidation && isRequired"
      :filter="filter"
      :showClear="showClear"
      :maxSelectedLabels="maxSelectedLabels"
      :selectAll="selectAll"
      :display="display"
      @change="onChange"
      fluid
      class="w-full"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import MultiSelect from 'primevue/multiselect';
import { log } from '../utils/DebugLogger';

interface SelectItem {
  ID: string | number;
  Name: string;
  [key: string]: any;
}

const props = defineProps({
  // Optional callback function triggered when selection changes
  callback: Function as import('vue').PropType<() => void>,

  // Array of objects with {ID, Name} structure for dropdown options
  itensSelect: {
    type: Array as import('vue').PropType<SelectItem[]>,
    default: () => []
  },

  // Unique identifier for the select element
  elemId: String,

  // Placeholder text shown when no option is selected
  placeholder: String,

  // Optional label text displayed above the select
  label: String,

  // Validation function that returns boolean (true = valid, false = invalid)
  validation: {
    type: Function as import('vue').PropType<() => boolean>,
    default: () => true
  },

  // Indicates if the field is required (shows warning icon when invalid)
  ngRequired: {
    type: [String, Boolean],
    default: false
  },

  // Indicates if the field should be disabled
  disabled: {
    type: [String, Boolean],
    default: false
  },

  // Current selected values array (v-model support)
  modelValue: {
    type: Array as import('vue').PropType<(string | number)[]>,
    default: () => []
  },

  // Legacy value prop for backward compatibility
  value: {
    type: Array as import('vue').PropType<(string | number)[]>,
    default: () => []
  },

  // Display mode: 'comma' or 'chip'
  display: {
    type: String,
    default: 'comma',
    validator: (value: string) => ['comma', 'chip'].includes(value)
  },

  // Maximum number of selected labels to show before using "{n} items selected" format
  maxSelectedLabels: {
    type: Number,
    default: 3
  },

  // Enable/disable filtering
  filter: {
    type: Boolean,
    default: true
  },

  // Show clear button
  showClear: {
    type: Boolean,
    default: false
  },

  // Enable/disable select all
  selectAll: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: (string | number)[]];
  'input': [value: (string | number)[]];
}>();

// Reactive data
const selectedItems = ref<(string | number)[]>([]);

// Computed properties
const isRequired = computed((): boolean =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  props.validation()
);

// Methods
const onChange = (): void => {
  const newValue = selectedItems.value || [];
  log('SelectListMultiple', 'onChange', {
    selectedItems: selectedItems.value,
    emittedValue: newValue
  });

  emit('update:modelValue', newValue);
  emit('input', newValue); // Keep for backward compatibility

  if (props.callback) {
    props.callback();
  }
};

const syncSelectedItems = (value: (string | number)[] | null | undefined): void => {
  log('SelectListMultiple', 'syncSelectedItems', 'Syncing selected items with value:', value);

  if (!value || !Array.isArray(value)) {
    selectedItems.value = [];
  } else {
    selectedItems.value = [...value];
  }
};

// Watchers
watch(() => props.modelValue, (newVal) => {
  log('SelectListMultiple', 'watch:modelValue', 'modelValue changed:', newVal);
  syncSelectedItems(newVal);
}, { deep: true });

watch(() => props.value, (newVal) => {
  log('SelectListMultiple', 'watch:value', 'value changed:', newVal);
  syncSelectedItems(newVal);
}, { deep: true });

watch(() => props.itensSelect, () => {
  log('SelectListMultiple', 'watch:itensSelect', 'itensSelect changed:', props.itensSelect);
  // Re-validate selected items against new list
  nextTick(() => {
    const currentValue = props.modelValue || props.value;
    if (currentValue && currentValue.length > 0) {
      // Filter out any selected IDs that are no longer in the available items
      const availableIds = props.itensSelect.map(item => item.ID);
      const validSelectedIds = currentValue.filter(id => availableIds.includes(id));
      
      if (validSelectedIds.length !== currentValue.length) {
        log('SelectListMultiple', 'watch:itensSelect', 'Some selected items are no longer available, updating selection');
        selectedItems.value = validSelectedIds;
        onChange();
      } else {
        syncSelectedItems(currentValue);
      }
    }
  });
}, { deep: true });

// Initialize on mount
nextTick(() => {
  log('SelectListMultiple', 'mounted', 'Component mounted', {
    modelValue: props.modelValue,
    value: props.value,
    itensSelect: props.itensSelect
  });
  
  const initialValue = props.modelValue || props.value;
  if (initialValue && Array.isArray(initialValue) && initialValue.length > 0) {
    syncSelectedItems(initialValue);
  }
});
</script>

<style scoped>
.select-list-multiple-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.select-list-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--text-color);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.select-list-label.text-warning {
  color: var(--yellow-500);
}

.select-list-label.text-success {
  color: var(--green-500);
}

.required-indicator {
  color: var(--red-500);
  margin-right: 0.25rem;
}

.text-warning {
  color: var(--yellow-500);
}

.text-success {
  color: var(--green-500);
}
</style>

